import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  closeQnA, deleteQuestion, updateQuestion, submitQuestion, verifyQuestions,
  setIsSignupSidebarOpen,
} from '@alumni_v2/stores/reviews/actions';
import { isUserLoggedIn, setAuthData } from '@alumni_v2/utils/helpers';
import { Modal, GTMTracking, Turnstile } from '@common/ui/general';
import { QA_TURNSTILE_WIDGET_ID } from '@alumni_v2/utils/constants';
import { qnaSelector } from '@alumni_v2/selectors/reviews';
import { trackingSubmitQnAForm, trackingQnAForm }
  from '@alumni_v2/utils/tracking';
import { useMediaQuery } from '@common/hooks';

import styles from './QnA.module.scss';

function QnA() {
  const dispatch = useDispatch();
  const { tablet } = useMediaQuery();

  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState();
  const [editIdx, setEditIdx] = useState();
  const [addQuestion, setAddQuestion] = useState(false);
  const { isOpen, questions, askedToId } = useSelector(qnaSelector) || {};

  useEffect(() => {
    setCurrentQuestion('');
    setEditIdx(null);
    setAddQuestion(false);
  }, [isOpen]);

  const onDelete = useCallback((e, questionId) => {
    e.preventDefault();
    dispatch(deleteQuestion(askedToId, questionId, recaptchaToken));
  }, [dispatch, askedToId, recaptchaToken]);

  const onSubmitQuestion = useCallback((e) => {
    e.preventDefault();
    dispatch(submitQuestion(askedToId, currentQuestion, recaptchaToken));
    setAddQuestion(false);
  }, [dispatch, askedToId, currentQuestion, recaptchaToken]);

  const onUpdateQuestion = useCallback(() => {
    dispatch(
      updateQuestion(
        askedToId, questions[editIdx].id, currentQuestion, recaptchaToken,
      ),
    );
    setEditIdx(null);
    setAddQuestion(false);
  }, [
    dispatch, askedToId, currentQuestion,
    editIdx, questions, recaptchaToken,
  ]);

  const onChange = useCallback((e) => {
    setCurrentQuestion(e.target.value);
  }, []);

  const onEdit = useCallback((idx) => {
    setCurrentQuestion(questions[idx].attributes.description);
    setEditIdx(idx);
  }, [questions]);

  const onCloseQnAModal = useCallback(() => {
    dispatch(closeQnA());
  }, [dispatch]);

  const addQuestionHandler = useCallback((e) => {
    e.preventDefault();
    setAddQuestion(true);
    setCurrentQuestion('');
  }, []);

  const postVerificationCallback = useCallback((status, msg) => {
    trackingSubmitQnAForm(status, msg);
  }, []);

  const onFinalSubmitQuestion = useCallback((e) => {
    e.preventDefault();
    if (isUserLoggedIn()) {
      dispatch(verifyQuestions(askedToId, postVerificationCallback));
    } else {
      setAuthData(askedToId);
      dispatch(setIsSignupSidebarOpen(true));
      document.getElementsByClassName('alumni-v2-auth-trigger')[0].click();
      onCloseQnAModal();
      trackingSubmitQnAForm(true, 'Please login/signup to proceed');
    }
  }, [dispatch, onCloseQnAModal, postVerificationCallback, askedToId]);

  const recaptchaCallback = useCallback((token) => {
    setRecaptchaToken(token);
  }, []);

  const onClickHandler = useCallback((e, idx) => {
    trackingQnAForm(e, idx, questions);
  }, [questions]);

  const buttonText = isUserLoggedIn()
    ? 'Submit your questions'
    : 'Proceed to login or signup';

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseQnAModal}
      position={tablet ? 'bottom' : 'right'}
      className={styles.containerUi}
      containerClassName={styles.bodyUi}
      headerClassName={styles.crossHeader}
    >
      <div className={styles.asqQuesContainer}>
        <div className={styles.askQueStepContainer}>
          <div className={styles.askQueStep}>
            <div className={styles.askQueStepNumber}>1</div>
            <div className={styles.askQueStepName}>
              Ask your questions
            </div>
          </div>
          {!isUserLoggedIn() && (
            <div className={styles.stepIndication}>
              <div className={styles.askQueStepIndicator} />
              <div className={styles.askQueStep}>
                <div className={styles.askQueBlurStepNumber}>2</div>
                <div className={styles.askQueBlurStepName}>
                  Login/Signup & Submit
                </div>
              </div>
            </div>
          )}
        </div>

        <form className={styles.askQueForm}>
          <div className={styles.formHeading}>
            Hey, you can ask upto 2 questions to the alumni
          </div>
          <div className={styles.formIntroText}>
            Be specific and and include all necessary details to help our
            alumni answer your question
          </div>

          <div>
            {questions.map((question, idx) => (editIdx === idx ? (
              <div key={idx}>
                <label
                  htmlFor="update-question"
                  className={styles.questionIndex}
                >
                  Ask your
                  {' '}
                  {idx + 1}
                  {' '}
                  question
                </label>
                <textarea
                  className={styles.inputTextarea}
                  id="update-question"
                  name="textarea"
                  placeholder="Eg: How to switch from service to
                     product companies?"
                  maxLength="512"
                  value={currentQuestion}
                  onChange={onChange}
                  onClick={(e) => onClickHandler(e, idx)}
                />
                <GTMTracking
                  component="button"
                  gtmEventType="cta"
                  gtmEventAction="click"
                  gtmEventResult="update_question"
                  gtmEventCategory="alumni_directory"
                  gtmEventData={{
                    click_text: 'update_question',
                    click_type: 'form_fields',
                    click_section: 'qna_sidebar',
                  }}
                  className={
                    `${styles.questionStoreBtn}
                    ${currentQuestion ? '' : styles.disabledQuestionStoreBtn}`
                  }
                  onClick={onUpdateQuestion}
                  role="button"
                  tabIndex={0}
                >
                  Update
                </GTMTracking>
              </div>
            ) : (
              <div key={idx} className={styles.submittedQueContainer}>
                <div className={styles.queNumber}>
                  Question
                  {' '}
                  {idx + 1}
                </div>
                <div className={styles.submittedQuestion} key={idx}>
                  <div className={styles.updatedQue}>
                    {question.attributes.description}
                  </div>
                  <div className={styles.submitedQueButtons}>
                    <GTMTracking
                      component="button"
                      gtmEventType="cta"
                      gtmEventAction="click"
                      gtmEventResult="edit_question_button"
                      gtmEventCategory="alumni_directory"
                      gtmEventData={{
                        click_text: 'edit_question_button',
                        click_type: 'form_fields',
                        click_section: 'qna_sidebar',
                      }}
                      className={styles.submitedQueBtn}
                      onClick={() => onEdit(idx)}
                      tabIndex={-1}
                      role="button"
                    >
                      edit
                    </GTMTracking>
                    <GTMTracking
                      component="button"
                      gtmEventType="cta"
                      gtmEventAction="click"
                      gtmEventResult="delete_question_button"
                      gtmEventCategory="alumni_directory"
                      gtmEventData={{
                        click_text: 'delete_question_button',
                        click_type: 'form_fields',
                        click_section: 'qna_sidebar',
                      }}
                      className={styles.submitedQueBtn}
                      onClick={(e) => onDelete(e, question.id)}
                      tabIndex={-1}
                      role="button"
                    >
                      delete
                    </GTMTracking>
                  </div>
                </div>
              </div>
            )))}
            {/* add a limit of max 2 questions */}
            <div className={styles.askQueArea}>
              {(
                !editIdx
                && (addQuestion || questions.length === 0)
                && questions.length < 2
              ) && (
                <>
                  <label htmlFor="question" className={styles.questionIndex}>
                    Ask your
                    {' '}
                    {questions.length + 1}
                    {' '}
                    question
                  </label>
                  <textarea
                    className={styles.inputTextarea}
                    id="question"
                    name="textarea"
                    placeholder="Eg: How to switch from service to
                     product companies?"
                    maxLength="512"
                    value={currentQuestion}
                    onChange={onChange}
                    onClick={(e) => onClickHandler(e, questions.length)}
                  />
                  <GTMTracking
                    component="button"
                    gtmEventType="cta"
                    gtmEventAction="click"
                    gtmEventResult={`submit_question_${questions.length + 1}`}
                    gtmEventCategory="alumni_directory"
                    gtmEventData={{
                      click_text: 'submit_question',
                      click_type: 'form_fields',
                      click_section: 'qna_sidebar',
                    }}
                    className={
                      `${styles.questionStoreBtn}
                    ${currentQuestion ? '' : styles.disabledQuestionStoreBtn}`
                    }
                    onClick={onSubmitQuestion}
                  >
                    Done
                  </GTMTracking>
                </>
              )}
            </div>
            {questions.length < 2 ? (
              <GTMTracking
                component="button"
                gtmEventType="cta"
                gtmEventAction="click"
                gtmEventResult="add_question"
                gtmEventCategory="alumni_directory"
                gtmEventData={{
                  click_text: 'add_question',
                  click_type: 'form_fields',
                  click_section: 'qna_sidebar',
                }}
                onClick={addQuestionHandler}
                role="button"
                tabIndex={0}
                className={
                  `${styles.addQueBtn}
                  ${questions.length === 1 ? '' : styles.disabledAddQueBtn}`
                }
              >
                + Add 1 more question
              </GTMTracking>
            ) : (
              <div className={styles.maxLimitText}>
                You have asked the maximum number of questions
              </div>
            )}
            <Turnstile
              widgetId={QA_TURNSTILE_WIDGET_ID}
              action="qna_ask_question"
              onVerify={recaptchaCallback}
              data-turnstile-appearance="always"
            />
            <GTMTracking
              component="button"
              gtmEventType="cta"
              gtmEventAction="click"
              gtmEventResult={buttonText}
              gtmEventCategory="alumni_directory"
              gtmEventData={{
                click_text: `${buttonText}`,
                click_type: 'form_fields',
                click_section: 'qna_sidebar',
              }}
              className={
                `${styles.submitButton}
              ${questions.length > 0 ? '' : styles.disabledQuestionStoreBtn}`
              }
              onClick={onFinalSubmitQuestion}
              role="button"
              tabIndex={0}
            >
              {isUserLoggedIn()
                ? 'Submit your questions' : 'Proceed to login or signup'}
            </GTMTracking>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default QnA;
